import {
  GetPaperlessSettingsResponse,
  PaperlessServiceScript,
  Script,
  ScriptType,
} from "../../api/models";

const paperlessServiceScriptSS0002 = (): Script[] => [
  {
    scriptType: ScriptType.Instruction,
    instructions: [
      {
        text: "If not already verified, verify Cardmember billing address.",
      },
    ],
  },
  {
    scriptType: ScriptType.Dialogue,
    dialogue:
      "All customers who have chosen to receive online billing statements in lieu of instead of receiving billing statements " +
      "in the mail must agree to receive an email notification each month notifying them that their billing statement is available.\n\n" +
      "So, if you want to stop receiving online statement e-mail notifications, you will again receive billing statements in the mail.\n\n" +
      "Is that OK with you?",
  },
  {
    scriptType: ScriptType.Instruction,
    instructions: [
      {
        text: "If want to stay on paperless, Click Close button.\n\n",
      },
      {
        text: "If OK. Continue reading script below.",
      },
    ],
  },
  {
    scriptType: ScriptType.Dialogue,
    dialogue:
      "We will remove your email address from our online statement mailing system so that you will no longer receive online statement email notifications. " +
      "But, we may send you other email notifications about your account, for example for collection purposes.\n\n" +
      "Is that OK with you?",
  },
  {
    scriptType: ScriptType.Instruction,
    instructions: [
      { text: "If OK. Click Update button.\n\n" },
      { text: "If Not OK, Continue reading script below." },
    ],
  },
  {
    scriptType: ScriptType.Dialogue,
    dialogue:
      "The Bank is committed to ensure that your privacy is protected and as a result we do not provide your email address(es) to third parties. " +
      "Additionally, the Bank only sends email notifications about your account or new services we offer. " +
      "Are you sure you do not want to receive information from us via email?",
  },
  {
    scriptType: ScriptType.Instruction,
    instructions: [
      {
        text: "If wants to opt-out, check Opt Out of Email Contact Checkbox. Click Update button.\n\n",
      },
      { text: "If does not want to opt-out. Click Update button." },
    ],
  },
];

const paperlessServiceScriptSS0003 = (): Script[] => [
  {
    scriptType: ScriptType.Instruction,
    instructions: [
      {
        text: "If not already verified, verify Cardmember billing address.",
      },
    ],
  },
  {
    scriptType: ScriptType.Dialogue,
    dialogue:
      "Even if you choose to receive billing statements by mail, " +
      "you will continue to receive e-mail notifications when your billing statements are available online.\n\n" +
      "Is that OK with you?",
  },
  {
    scriptType: ScriptType.Instruction,
    instructions: [
      { text: "If OK. Click Update button.\n\n" },
      {
        text: "If Not OK, Check the Do Not Send Online Statement e-mail checkbox. Continue reading script below.",
      },
    ],
  },
  {
    scriptType: ScriptType.Dialogue,
    dialogue:
      "We will remove your email address from our online statement mailing system so that you will no longer receive online statement email notifications. " +
      "But, we may send you other email notifications about your account, for example for collection purposes.\n\n" +
      "Is that OK with you?",
  },
  {
    scriptType: ScriptType.Instruction,
    instructions: [
      { text: "If OK. Click Update button.\n\n" },
      { text: "If Not OK, Continue reading script below." },
    ],
  },
  {
    scriptType: ScriptType.Dialogue,
    dialogue:
      "The Bank is committed to ensure that your privacy is protected and as a result we do not provide your email address(es) to third parties. " +
      "Additionally, the Bank only sends email notifications about your account or new services we offer. " +
      "Are you sure you do not want to receive information from us via email?",
  },
  {
    scriptType: ScriptType.Instruction,
    instructions: [
      {
        text: "If wants to opt-out click Update button and then access the CM Info tool, check Opt Out of Email Contact checkbox, and click Update.\n\n",
      },
      { text: "If does not want to opt-out. Click Update button." },
    ],
  },
];

const paperlessServiceScriptSS0004 = (
  primaryEmail?: string | null,
  alternateEmail?: string | null,
  canUpdate?: boolean,
): (Script | null)[] => [
  primaryEmail
    ? {
        scriptType: ScriptType.Instruction,
        instructions: [
          {
            text: "Have the Cardmember spell the email address if it is difficult",
          },
        ],
      }
    : null,
  primaryEmail
    ? {
        scriptType: ScriptType.Dialogue,
        dialogue: `We currently have an email address at ${primaryEmail.split("@")[1]} for you on file. Would you please verify that email address?`,
      }
    : null,
  !primaryEmail && !alternateEmail
    ? {
        scriptType: ScriptType.Dialogue,
        dialogue:
          "And what email address would you like to receive the online statement email notifications?",
      }
    : null,
  !primaryEmail && !alternateEmail
    ? {
        scriptType: ScriptType.Instruction,
        instructions: [
          {
            text: "Confirm the primary email address by reading the address back to Cardmember. Spell the address if it is difficult.",
          },
        ],
      }
    : null,
  {
    scriptType: ScriptType.Instruction,
    instructions: [{ text: `Click ${canUpdate ? "Update" : "Close"} button.` }],
  },
];

function notNull<T>(value: T | null): value is T {
  return value !== null;
}

const paperlessServiceScriptSS0005 = (
  prevStopPaperless: boolean,
  currStopPaperless: boolean,
  prevOnlineStatementOptOut: boolean,
  currStopOnlineStatementOptOut: boolean,
): Script[] => {
  const scripts: Script[] = [];

  // Paperless Service has just been STOPPED
  if (prevStopPaperless === false && currStopPaperless === true) {
    scripts.push({
      scriptType: ScriptType.Dialogue,
      dialogue:
        "It may take up to 2 statement billing cycles before you start receiving billing statements in the mail again.",
    });
  }

  // Online Statement has just been OPTED OUT OF
  if (
    prevOnlineStatementOptOut === false &&
    currStopOnlineStatementOptOut === true
  ) {
    scripts.push(
      {
        scriptType: ScriptType.Dialogue,
        dialogue:
          "It may take up to 7 days before you stop receiving online statement email notifications from us.",
      },
      {
        scriptType: ScriptType.Dialogue,
        dialogue:
          "In the future, if you would like to receive online statement email notifications, please let us know and we will update your profile.",
      },
    );
  }

  // Online Statement has just been OPTED INTO
  if (
    prevOnlineStatementOptOut === true &&
    currStopOnlineStatementOptOut === false
  ) {
    scripts.push({
      scriptType: ScriptType.Dialogue,
      dialogue:
        "It may take up to 7 days before you start receiving online statement email notifications from us.",
    });
  }

  scripts.push(
    {
      scriptType: ScriptType.Dialogue,
      dialogue: "Is there anything else I can help you with?",
    },
    {
      scriptType: ScriptType.Instruction,
      instructions: [
        {
          text: "Click Close to end",
        },
      ],
    },
  );

  return scripts;
};

export type PaperlessServiceScriptDetails = {
  name: PaperlessServiceScript;
  scripts: Script[];
};

type PaperlessServiceStates = {
  initial?: GetPaperlessSettingsResponse;
  updated?: GetPaperlessSettingsResponse;
};

export const getPaperlessServiceScriptDetails = (
  scriptName: PaperlessServiceScript,
  states?: PaperlessServiceStates,
): PaperlessServiceScriptDetails => {
  switch (scriptName) {
    case "SS_0002":
      return {
        name: "SS_0002",
        scripts: paperlessServiceScriptSS0002(),
      };
    case "SS_0003":
      return {
        name: "SS_0003",
        scripts: paperlessServiceScriptSS0003(),
      };
    case "SS_0004":
      return {
        name: "SS_0004",
        scripts: paperlessServiceScriptSS0004(
          states?.initial?.email.primaryEmail,
          states?.initial?.email.alternateEmail,
          states?.initial?.paperlessServiceOptOut !==
            states?.updated?.paperlessServiceOptOut ||
            states?.initial?.onlineStatementEmailOptOut !==
              states?.updated?.onlineStatementEmailOptOut,
        ).filter(notNull),
      };
    case "SS_0005":
      return {
        name: "SS_0005",
        scripts: paperlessServiceScriptSS0005(
          states?.initial?.paperlessServiceOptOut ?? false,
          states?.updated?.paperlessServiceOptOut ?? false,
          states?.initial?.onlineStatementEmailOptOut ?? false,
          states?.updated?.onlineStatementEmailOptOut ?? false,
        ),
      };
  }
};
